import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@src/lib/utils/cn";
import AskOpusOrb from "@src/features/assistantChat/AskOpusOrb";
import type { OrbStateId } from "@src/features/assistantChat/afterimage/orb";
import { useOrbState } from "@src/features/assistantChat/useAskOpusOrbState";
import { OpusAgentAvatarKind } from "@src/types.generated";
import { orbNoEyes } from "@src/features/assistantChat/afterimage/orb";
import { hashId } from "./botOrb";
import { AGENT_COLORS } from "./agentColors";

export { AGENT_COLORS };

const SIZE_PX = { sm: 16, icon: 20, list: 24, md: 32, lg: 48 } as const;

export type AgentColor = (typeof AGENT_COLORS)[number];
export type AgentColorSlug = AgentColor["slug"];

const isAgentColor = (value: unknown): value is AgentColorSlug =>
  AGENT_COLORS.some((color) => color.slug === value);

// Agents saved with one of the old pastel tints keep a close brand colour.
const LEGACY_TINTS: Record<string, AgentColorSlug> = {
  lilac: "purple",
  sky: "cobalt",
  mint: "aqua",
  sage: "teal",
  butter: "amber",
  peach: "orange",
  blush: "orange",
};

/** A stable colour for an id (an agent's, or the template it came from). */
export const defaultAgentColor = (id: string): AgentColorSlug =>
  AGENT_COLORS[hashId(id) % AGENT_COLORS.length].slug;

/** A new agent's colour, saved with it. */
export const randomAgentColor = (): AgentColorSlug =>
  AGENT_COLORS[Math.floor(Math.random() * AGENT_COLORS.length)].slug;

const colorOf = (slug: AgentColorSlug): AgentColor =>
  AGENT_COLORS.find((color) => color.slug === slug) ?? AGENT_COLORS[0];

/** The avatar fields of OpusAgentFields that the picture is drawn from. */
export type AgentAvatarFields = {
  id: string;
  avatarKind?: OpusAgentAvatarKind | null;
  avatarBotShape?: string | null;
  avatarBotColor?: string | null;
  avatarImageUrl?: string | null;
  template?: { id: string } | null;
};

/** The agent's saved colour (an old tint maps to its brand colour), else a
 * stable one: its template's (so it keeps the bot its gallery card showed),
 * or its own. */
export const agentColor = (agent: AgentAvatarFields): AgentColorSlug => {
  const saved =
    agent.avatarKind === OpusAgentAvatarKind.Bot ? agent.avatarBotColor : null;
  if (isAgentColor(saved)) return saved;
  if (saved && Object.hasOwn(LEGACY_TINTS, saved)) return LEGACY_TINTS[saved];
  return defaultAgentColor(agent.template?.id ?? agent.id);
};

/** The agent's uploaded or generated picture, when it has one. */
export const agentImageUrl = (agent: AgentAvatarFields): string | null =>
  agent.avatarKind === OpusAgentAvatarKind.Image
    ? agent.avatarImageUrl ?? null
    : null;
export type AgentAvatarSize = keyof typeof SIZE_PX;

// How strong a bot's texture is; agents ship Subtle (the /ui/agent
// playground compares them).
export const BOT_TEXTURES = {
  off: null,
  subtle: { grain: 0.12, mottle: 0.28, specks: 0.45 },
  bold: { grain: 0.24, mottle: 0.55, specks: 0.85 },
} as const;
export type BotTexture = keyof typeof BOT_TEXTURES;

// How blob-like a bot's edge is: a soft, slowly moving outline instead of
// a perfect circle ("Wobbly" in the /ui/agent playground; 1 is "More").
export const BOT_WOBBLE = 0.6;

type BotOrbProps = {
  /** The bot's brand colour. */
  color: AgentColorSlug;
  /** Texture strength; Subtle unless a comparison asks otherwise. */
  texture?: BotTexture;
  /** How blob-like the edge is, 0 (a circle) to 1; BOT_WOBBLE by default. */
  wobble?: number;
  /** Rendered size in px. */
  size: number;
  state?: OrbStateId;
  /** One still frame per state, never animated. */
  still?: boolean;
  label?: string;
  className?: string;
};

/**
 * A bot: the Ask Opus orb with no eyes, in a brand colour with a grainy,
 * slowly swirling texture. At rest the body holds still and only the
 * texture moves; it does the orb's thinking dots, "!", check and spin.
 */
export const BotOrb: React.FC<BotOrbProps> = ({
  color,
  texture = "subtle",
  wobble = BOT_WOBBLE,
  size,
  state = "idle",
  still,
  label,
  className,
}) => {
  const c = colorOf(color);
  const index = AGENT_COLORS.indexOf(c);
  const level = BOT_TEXTURES[texture];
  return (
    <AskOpusOrb
      size={size}
      state={state}
      still={still}
      object={orbNoEyes}
      calm
      wobble={wobble}
      fill={[c.color, c.edge]}
      // With texture off the seed still varies the wobbly edge per colour.
      texture={{
        ...(level ?? { grain: 0, mottle: 0, specks: 0 }),
        speck: c.speck,
        seed: 3.1 * (index + 1),
      }}
      label={label}
      className={className}
    />
  );
};

// One spin: the whirl state's hold and loop (orb.js), then the bot rests.
const HOVER_SPIN_MS = 2300;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A gallery bot greeting the pointer: on hover (or keyboard focus) it spins
 * once; a spin under way finishes after the pointer leaves. No second spin
 * until it has settled and is hovered again. Reduced motion: no spin.
 */
export const useHoverSpin = (): {
  state: OrbStateId;
  onEnter: () => void;
  onLeave: () => void;
} => {
  const [spinning, setSpinning] = useState(false);
  const spinningRef = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onEnter = useCallback(() => {
    if (spinningRef.current || prefersReducedMotion()) return;
    spinningRef.current = true;
    setSpinning(true);
    timer.current = setTimeout(() => {
      spinningRef.current = false;
      setSpinning(false);
    }, HOVER_SPIN_MS);
  }, []);
  // A spin always finishes, so leaving changes nothing; callers wire it
  // for symmetry with onEnter.
  const onLeave = useCallback(() => {}, []);
  return { state: spinning ? "whirl" : "idle", onEnter, onLeave };
};

type AgentBotProps = {
  agent: AgentAvatarFields;
  /** Rendered size in px. */
  size: number;
  state?: OrbStateId;
  still?: boolean;
  label?: string;
  className?: string;
};

/**
 * An agent's picture at any size: its uploaded or generated image, round,
 * or its bot in its colour. The image doesn't animate; the bot does unless
 * still.
 */
export const AgentBot: React.FC<AgentBotProps> = ({
  agent,
  size,
  state,
  still,
  label,
  className,
}) => {
  const imageUrl = agentImageUrl(agent);
  // A picture that fails to load (deleted, replaced by a non-image) falls
  // back to the bot; a new URL gets a fresh try.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (imageUrl && imageUrl !== failedUrl)
    return (
      <img
        src={imageUrl}
        alt={label ?? ""}
        aria-hidden={label ? undefined : true}
        width={size}
        height={size}
        className={cn("shrink-0 rounded-full object-cover", className)}
        style={{ width: size, height: size }}
        onError={() => setFailedUrl(imageUrl)}
        data-testid="agent-avatar-image"
      />
    );
  return (
    <BotOrb
      color={agentColor(agent)}
      size={size}
      state={state}
      still={still}
      label={label}
      className={className}
    />
  );
};

type Props = {
  agent: AgentAvatarFields;
  size?: AgentAvatarSize;
  /** Defaults to the agent's live state (thinking while its chat runs). */
  state?: OrbStateId;
  label?: string;
  className?: string;
};

/**
 * An agent's avatar in the nav and panel header: its image, or its bot in
 * its colour. It never animates: a bot shows its live state as a still frame
 * (at rest, or the Ask Opus fill while busy). Only the chat's bots move.
 */
const AgentAvatar: React.FC<Props> = ({
  agent,
  size = "md",
  state,
  label,
  className,
}) => {
  const live = useOrbState(agent.id);
  return (
    <AgentBot
      agent={agent}
      size={SIZE_PX[size]}
      state={state ?? live}
      still
      label={label}
      className={className}
    />
  );
};

export default AgentAvatar;
